<template>
	<div
		ref="$editorRoot"
		class="inline-address-editor"
	>
		<template v-if="alwaysEditing">
			<address-selector-refactored
				ref="$selector"
				:id="id"
				:key="editedAddress.Id"
				v-model="editedAddress"
				:instant-init="true"
				:menu-z-index="menuZIndex"
				:menu-anchor="$editorRoot"
				@chainSelected="onChain"
			/>
			<span
				v-if="hasAddress"
				class="inline-address-editor__actions"
			>
				<app-tooltip :text="$t('clear_field')">
					<template #activator>
						<app-icon
							class="inline-address-editor__btn"
							name="arrow-left"
							width="18px"
							color="#979797"
							hover-color="#499259"
							cursor="pointer"
							@click="clearAlways"
						/>
					</template>
				</app-tooltip>
			</span>
		</template>
		<template v-else-if="isEditing">
			<address-selector-refactored
				:id="id"
				:key="editedAddress.Id"
				v-model="editedAddress"
				:instant-init="true"
				:menu-z-index="menuZIndex"
				:menu-anchor="$editorRoot"
				@chainSelected="onChain"
			/>
			<span class="inline-address-editor__actions">
				<app-tooltip :text="$t('save')">
					<template #activator>
						<app-icon
							class="inline-address-editor__btn"
							name="check-circle"
							width="18px"
							color="#979797"
							hover-color="#499259"
							cursor="pointer"
							@click="saveAddress"
						/>
					</template>
				</app-tooltip>
				<app-tooltip :text="$t('clear_field')">
					<template #activator>
						<app-icon
							class="inline-address-editor__btn"
							name="arrow-left"
							width="18px"
							color="#979797"
							hover-color="#499259"
							cursor="pointer"
							@click="clearAddress"
						/>
					</template>
				</app-tooltip>
				<app-tooltip :text="$t('cancel_short')">
					<template #activator>
						<app-icon
							class="inline-address-editor__btn"
							name="close"
							width="18px"
							color="#979797"
							hover-color="#499259"
							cursor="pointer"
							@click="cancelEdit"
						/>
					</template>
				</app-tooltip>
			</span>
		</template>
		<template v-else>
			<span class="inline-address-editor__name">
				{{ displayText }}
			</span>
			<span
				v-if="editable"
				class="inline-address-editor__actions inline-address-editor__actions--view"
			>
				<app-tooltip :text="$t('edit_label')">
					<template #activator>
						<app-icon
							class="inline-address-editor__btn"
							name="edit-pencil"
							width="18px"
							color="#979797"
							hover-color="#499259"
							cursor="pointer"
							@click="enterEdit"
						/>
					</template>
				</app-tooltip>
			</span>
		</template>
	</div>
</template>

<script setup lang="ts">
import {
	computed,
	nextTick,
	onMounted,
	ref,
	watch
} from "vue";
import AddressSelectorRefactored from "@/components/common/address-selector/AddressSelectorRefactored.vue";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import AppTooltip from "@/components/ui-kit/tooltip/AppTooltip.vue";
import { CommonEntity } from "@/models/interfaces/common";
import { AddressInputComponentOut, AddressInputContainer } from "@/models/interfaces/entity-settings";
import { auxStore } from "@/store/auxillary";
import { chainToRequestContainer, RequestAddressContainer } from "@/composables/useAddressRequest";

const props = withDefaults(defineProps<{
	modelValue: RequestAddressContainer | null,
	id: string,
	// false — read-only: текст без карандаша, вход в редактирование запрещён.
	editable?: boolean,
	// Показывать ли плейсхолдеры уровней, когда адрес не указан (только режим toggle):
	// true — видно "Страна, Регион, …"; false (страница Клиента) — пусто, только карандаш.
	showEmptyPlaceholder?: boolean,
	// z-index меню автокомплита (.fmenu). 0 — штатный 1000; поднимается только
	// когда редактор живёт внутри высокого по z-index дропдауна (окно контакта).
	menuZIndex?: number,
	// true (фильтры/окно контакта) — селектор всегда развёрнут, единственная кнопка
	// очистки, коммит на каждое изменение. false (страница Клиента) — режим toggle.
	alwaysEditing?: boolean,
}>(), {
	editable: true,
	showEmptyPlaceholder: true,
	menuZIndex: 0,
	alwaysEditing: false,
})

const emit = defineEmits<{
	// v-model для фильтров/формы (локальный payload).
	(e: 'update:modelValue', value: RequestAddressContainer | null): void,
	// сырая цепочка для серверной записи (страница Клиента).
	(e: 'save', chain: AddressInputContainer): void,
}>()

const isEditing = ref(false)
const editedAddress = ref<CommonEntity>({ Id: 0, Name: "" })
const latestChain = ref<AddressInputContainer | null>(null)
const $editorRoot = ref<HTMLElement | null>(null)
const $selector = ref<{
	clearAddressModel: () => void,
	emitAddressChain: () => void,
} | null>(null)

const structure = ref<AddressInputComponentOut[]>(auxStore().getCachedAddressContainer ?? [])
let baseline = ""
// Гасим собственное эхо update:modelValue, чтобы watcher не ресидил editedAddress
// (и не прерывал ввод/выбор пересозданием по :key). Повторяет guardModelSync селектора.
let echo = false

onMounted(async () => {
	await auxStore().getAddressContainerStructure()
	structure.value = auxStore().getCachedAddressContainer ?? []
	if (props.alwaysEditing) {
		seedEdited()
	}
})

watch(() => props.modelValue, () => {
	if (!props.alwaysEditing || echo) {
		return
	}
	seedEdited()
})

// Значения по уровням из текущего modelValue (по порядку структуры).
const valuesByStructure = (): string[] =>
	structure.value.map(
		s => props.modelValue?.components.find(c => c.level === s.Level)?.value ?? ""
	)

// Текст view-режима: непустые значения, либо плейсхолдеры, если адрес не указан.
const displayText = computed(() => {
	if (!structure.value.length) {
		return ""
	}
	const values = valuesByStructure()
	if (values.some(v => v.trim().length > 0)) {
		return values.filter(v => v.trim().length > 0).join(", ")
	}
	if (!props.showEmptyPlaceholder) {
		return ""
	}
	return structure.value.map(s => s.Placeholder).join(", ")
})

const hasAddress = computed(() => !!props.modelValue && props.modelValue.components.length > 0)

const seedName = (): string => valuesByStructure().join(",")

const seedEdited = () => {
	editedAddress.value = { Id: 0, Name: seedName() }
}

const enterEdit = () => {
	if (!props.editable || isEditing.value) {
		return
	}
	baseline = seedName()
	editedAddress.value = { Id: 0, Name: baseline }
	latestChain.value = null
	isEditing.value = true
}

const blurAddressInput = () => {
	const active = document.activeElement as HTMLElement | null
	if (
		$editorRoot.value &&
		active &&
		$editorRoot.value.contains(active) &&
		typeof active.blur === "function"
	) {
		active.blur()
	}
}

const onChain = (p: { request: AddressInputContainer; correct: boolean }) => {
	latestChain.value = p.request

	if (props.alwaysEditing) {
		echo = true
		emit('update:modelValue', chainToRequestContainer(p.request))
		nextTick(() => {
			echo = false
		})
	}
}

const clearAlways = () => {
	$selector.value?.clearAddressModel?.()
}

const clearAddress = () => {
	editedAddress.value = { Id: 0, Name: "" }

	if (latestChain.value) {
		latestChain.value = {
			Components: latestChain.value.Components.map(c => ({ ...c, Value: "" })),
			ActiveLevel: 0,
		}
	}
}

const cancelEdit = () => {
	editedAddress.value = { Id: 0, Name: baseline }
	latestChain.value = null
	isEditing.value = false
	blurAddressInput()
}

const commit = () => {
	if (!latestChain.value) {
		return
	}
	emit('update:modelValue', chainToRequestContainer(latestChain.value))
	emit('save', latestChain.value)
}

const saveAddress = () => {
	commit()
	isEditing.value = false
	blurAddressInput()
}

const flush = () => {
	if (props.alwaysEditing) {
		// Селектор эмитит цепочку только на ввод/blur и один раз на инициализации:
		// внешний засев значения (открытие редактирования контакта) идёт через
		// modelWatcher и цепочку не обновляет. Поэтому просим пересобрать её из
		// живого container — иначе flush затрёт адрес устаревшим latestChain.
		// @Emit срабатывает синхронно, onChain успевает записать свежую цепочку.
		$selector.value?.emitAddressChain?.()
		emit('update:modelValue', chainToRequestContainer(latestChain.value))
		return
	}
	commit()
	isEditing.value = false
	blurAddressInput()
}

defineExpose({ flush })
</script>

<style scoped lang="scss">
.inline-address-editor {
	display: inline;
	font-size: 14px;
	font-weight: 400;
	line-height: 1.5;

	:deep(.token) {
		vertical-align: baseline;
	}

	&__name {
		font-size: 14px;
		line-height: 1.5;
		color: #4D4D4D;
	}

	&__actions {
		white-space: nowrap;
		margin-left: 2px;
	}

	&__btn {
		margin-left: 2px;
		vertical-align: middle;
	}
}
</style>
